import React, { useEffect, useState, useCallback } from "react";
import { Gift, Search, X, Plus, Mail, Copy, Power, Wallet, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ModalPortal } from "./ModalPortal";

const STATUS_FILTERS = [
    { slug: "all", label: "All" },
    { slug: "active", label: "Active" },
    { slug: "redeemed", label: "Redeemed" },
    { slug: "expired", label: "Expired" },
    { slug: "disabled", label: "Disabled" },
];

const STATUS_STYLES = {
    active: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    redeemed: "text-[var(--text-muted)] border-white/10 bg-white/5",
    expired: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    disabled: "text-red-300 border-red-500/40 bg-red-500/10",
};

export const AdminVouchersPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [stats, setStats] = useState(null);
    const [vouchers, setVouchers] = useState([]);
    const [total, setTotal] = useState(0);
    const [status, setStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, st] = await Promise.all([
                api.get("/admin/vouchers", { params: { status, search, limit: 50 }, headers }),
                api.get("/admin/vouchers/stats/overview", { headers }),
            ]);
            setVouchers(list.data.vouchers);
            setTotal(list.data.total);
            setStats(st.data);
        } catch {
            toast.error("Could not load vouchers");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, search, token]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-5" data-testid="admin-vouchers-panel">
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatBox label="Vouchers issued" value={stats.count} />
                    <StatBox label="Total issued" value={`A$${Number(stats.total_issued).toFixed(2)}`} />
                    <StatBox label="Outstanding" value={`A$${Number(stats.outstanding).toFixed(2)}`} highlight />
                    <StatBox label="Redeemed" value={`A$${Number(stats.total_redeemed).toFixed(2)}`} />
                </div>
            )}

            <div className="glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by code, recipient…"
                        className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="voucher-search-input"
                    />
                </div>
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="px-3 py-2 bg-black/40 border border-white/10 rounded-full text-xs font-mono uppercase tracking-widest text-white focus:outline-none focus:border-[var(--gold)]"
                    data-testid="voucher-status-filter"
                >
                    {STATUS_FILTERS.map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
                </select>
                <button onClick={() => setShowCreate(true)} className="btn-solid" data-testid="voucher-create-btn">
                    <Plus size={14} /> New voucher
                </button>
            </div>

            <div className="glass-elevated rounded-2xl overflow-hidden" data-testid="voucher-list">
                {loading && <div className="text-center text-[var(--text-secondary)] py-10 text-sm">Loading…</div>}
                {!loading && vouchers.length === 0 && (
                    <div className="text-center text-[var(--text-secondary)] py-12 text-sm">
                        <Gift size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                        No vouchers yet.
                    </div>
                )}
                {vouchers.map((v) => (
                    <button
                        key={v.id}
                        onClick={() => setSelected(v.id)}
                        className="w-full text-left grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr_120px] gap-3 px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                        data-testid={`voucher-row-${v.id}`}
                    >
                        <div>
                            <p className="font-mono text-sm text-white font-bold tracking-wider">{v.code}</p>
                            <p className="text-xs text-[var(--text-muted)] truncate">
                                {v.recipient_email || "No recipient"}
                            </p>
                        </div>
                        <div className="text-sm font-mono text-[var(--gold)]">A${Number(v.original_amount).toFixed(2)}</div>
                        <div className="text-sm font-mono text-emerald-300">A${Number(v.balance).toFixed(2)}</div>
                        <div>
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_STYLES[v.status] || ""}`}>
                                {v.status}
                            </span>
                        </div>
                        <div className="text-xs font-mono uppercase tracking-widest text-[var(--gold)] md:text-right">Manage →</div>
                    </button>
                ))}
                <div className="px-5 py-3 text-xs font-mono text-[var(--text-muted)]">{total} vouchers</div>
            </div>

            {showCreate && <VoucherCreateModal token={token} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
            {selected && <VoucherDetailDrawer voucherId={selected} token={token} onClose={() => setSelected(null)} onChanged={load} />}
        </div>
    );
};

const StatBox = ({ label, value, highlight = false }) => (
    <div className={`glass rounded-xl p-4 ${highlight ? "border border-[var(--gold)]/30" : ""}`}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</p>
        <p className={`font-display text-2xl font-extrabold italic tracking-tighter ${highlight ? "text-[var(--gold)]" : "text-white"}`}>{value}</p>
    </div>
);

const VoucherCreateModal = ({ token, onClose, onCreated }) => {
    const headers = { "X-Admin-Token": token };
    const [form, setForm] = useState({
        amount: "",
        recipient_email: "",
        recipient_name: "",
        sender_name: "Carbon Labs",
        message: "",
        expires_at: "",
        code: "",
        send_email: true,
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const amt = parseFloat(form.amount);
        if (!amt || amt <= 0) return toast.error("Enter an amount");
        setBusy(true);
        try {
            const body = {
                amount: amt,
                recipient_email: form.recipient_email || undefined,
                recipient_name: form.recipient_name || undefined,
                sender_name: form.sender_name || undefined,
                message: form.message || undefined,
                expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
                code: form.code || undefined,
                send_email: !!form.send_email && !!form.recipient_email,
            };
            const { data } = await api.post("/admin/vouchers", body, { headers });
            toast.success(`Voucher ${data.voucher.code} created`);
            onCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to create voucher");
        }
        setBusy(false);
    };

    return (
        <ModalPortal>
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="glass-elevated max-w-lg w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="voucher-create-modal">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">New gift voucher</p>
                        <h2 className="font-display text-2xl font-extrabold text-white italic tracking-tighter">Issue voucher</h2>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-white"><X size={18} /></button>
                </div>

                <div className="space-y-4">
                    <Field label="Amount (AUD) *">
                        <input
                            type="number" min="1" step="1"
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            placeholder="e.g. 50"
                            className="input"
                            data-testid="voucher-amount-input"
                        />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Recipient email">
                            <input
                                type="email"
                                value={form.recipient_email}
                                onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                                placeholder="friend@example.com"
                                className="input"
                                data-testid="voucher-recipient-email"
                            />
                        </Field>
                        <Field label="Recipient name">
                            <input
                                value={form.recipient_name}
                                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                                placeholder="Alex"
                                className="input"
                                data-testid="voucher-recipient-name"
                            />
                        </Field>
                    </div>
                    <Field label="Sender name (shown in email)">
                        <input
                            value={form.sender_name}
                            onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
                            className="input"
                        />
                    </Field>
                    <Field label="Message (optional, max 500 chars)">
                        <textarea
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value.slice(0, 500) })}
                            rows={3}
                            className="input"
                            placeholder="Happy birthday from the team…"
                        />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Expires (optional)">
                            <input
                                type="datetime-local"
                                value={form.expires_at}
                                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                                className="input"
                            />
                        </Field>
                        <Field label="Custom code (optional)">
                            <input
                                value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                placeholder="Auto-generated"
                                className="input font-mono tracking-widest"
                            />
                        </Field>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.send_email}
                            onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                            className="w-4 h-4 accent-[var(--gold)]"
                            data-testid="voucher-send-email"
                        />
                        <Mail size={14} /> Email voucher to recipient now
                    </label>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-white">Cancel</button>
                    <button onClick={submit} disabled={busy} className="btn-solid disabled:opacity-50" data-testid="voucher-submit">
                        {busy ? "Creating…" : "Create voucher"}
                    </button>
                </div>
            </div>

            <style>{`.input { width:100%; padding:8px 12px; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #fff; font-size: 14px; outline: none; }
                     .input:focus { border-color: var(--gold); }`}</style>
        </div>
        </ModalPortal>
    );
};

const Field = ({ label, children }) => (
    <div>
        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</p>
        {children}
    </div>
);

const VoucherDetailDrawer = ({ voucherId, token, onClose, onChanged }) => {
    const headers = { "X-Admin-Token": token };
    const [voucher, setVoucher] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/vouchers/${voucherId}`, { headers });
            setVoucher(data);
        } catch {
            toast.error("Could not load voucher");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voucherId]);

    useEffect(() => { reload(); }, [reload]);

    const toggleDisable = async () => {
        setBusy(true);
        const target = voucher.status === "disabled" ? "enable" : "disable";
        try {
            await api.post(`/admin/vouchers/${voucher.id}/${target}`, {}, { headers });
            toast.success(target === "disable" ? "Voucher disabled" : "Voucher re-enabled");
            await reload();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const resend = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/vouchers/${voucher.id}/resend`, {}, { headers });
            toast.success("Email resent");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const copyCode = () => {
        navigator.clipboard.writeText(voucher.code);
        toast.success("Code copied");
    };

    if (!voucher) {
        return (
            <ModalPortal>
            <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm">
                <div className="inline-block w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
            </div>
            </ModalPortal>
        );
    }

    return (
        <ModalPortal>
        <div className="fixed inset-0 z-[90] flex items-stretch justify-end bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl glass-elevated overflow-y-auto p-6 sm:p-8" data-testid="voucher-detail-drawer">
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Voucher</p>
                        <p className="font-mono text-2xl text-white font-bold tracking-widest mt-1 flex items-center gap-2">
                            {voucher.code}
                            <button onClick={copyCode} className="text-[var(--text-muted)] hover:text-white" data-testid="copy-voucher-code"><Copy size={14} /></button>
                        </p>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-white" data-testid="close-voucher-drawer"><X size={20} /></button>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                    <Summary label="Issued" value={`A$${Number(voucher.original_amount).toFixed(2)}`} />
                    <Summary label="Balance" value={`A$${Number(voucher.balance).toFixed(2)}`} highlight />
                    <Summary label="Status" value={voucher.status} />
                </div>

                <section className="glass rounded-2xl p-5 mb-5">
                    <h3 className="font-display text-lg font-bold text-white mb-3">Recipient</h3>
                    <p className="text-sm text-white">{voucher.recipient_name || "—"}</p>
                    <p className="text-xs text-[var(--text-muted)] font-mono">{voucher.recipient_email || "No email on file"}</p>
                    {voucher.message && (
                        <div className="mt-3 p-3 rounded-lg bg-[rgba(233,188,11,0.05)] border border-[var(--gold)]/20">
                            <p className="text-xs text-[var(--text-muted)] font-mono uppercase tracking-widest mb-1">Message</p>
                            <p className="text-sm text-white">{voucher.message}</p>
                        </div>
                    )}
                    {voucher.expires_at && (
                        <p className="text-xs text-amber-300 font-mono mt-3">
                            Expires {new Date(voucher.expires_at).toLocaleDateString("en-AU")}
                        </p>
                    )}
                    <div className="flex gap-2 mt-4">
                        {voucher.recipient_email && (
                            <button onClick={resend} disabled={busy} className="px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/30 disabled:opacity-40 inline-flex items-center gap-1" data-testid="voucher-resend-email">
                                <Send size={12} /> Resend email
                            </button>
                        )}
                        <button onClick={toggleDisable} disabled={busy} className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-1 disabled:opacity-40 ${voucher.status === "disabled" ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`} data-testid="voucher-toggle-disable">
                            <Power size={12} /> {voucher.status === "disabled" ? "Re-enable" : "Disable"}
                        </button>
                    </div>
                </section>

                <section className="glass rounded-2xl p-5">
                    <h3 className="font-display text-lg font-bold text-white mb-3 inline-flex items-center gap-2">
                        <Wallet size={16} /> Redemptions ({(voucher.redemptions || []).length})
                    </h3>
                    {(voucher.redemptions || []).length === 0 && (
                        <p className="text-sm text-[var(--text-secondary)] inline-flex items-center gap-2">
                            <AlertCircle size={14} /> Not yet redeemed.
                        </p>
                    )}
                    <div className="space-y-2">
                        {(voucher.redemptions || []).map((r, i) => (
                            <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-black/40 border border-white/5">
                                <div>
                                    <p className="text-sm text-white font-mono">{r.order_ref}</p>
                                    <p className="text-[10px] text-[var(--text-muted)] font-mono">
                                        {r.customer_email || "—"} · {new Date(r.at).toLocaleDateString("en-AU")}
                                    </p>
                                </div>
                                <p className="font-mono text-red-300 font-bold">−A${Number(r.amount).toFixed(2)}</p>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
        </ModalPortal>
    );
};

const Summary = ({ label, value, highlight = false }) => (
    <div className={`rounded-xl p-3 ${highlight ? "bg-[rgba(233,188,11,0.06)] border border-[var(--gold)]/30" : "bg-black/40 border border-white/5"}`}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</p>
        <p className={`font-mono font-bold text-base capitalize ${highlight ? "text-[var(--gold)]" : "text-white"}`}>{value}</p>
    </div>
);
